import { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Grid,
  IconButton,
  Tabs,
  Table,
  Text,
  TextArea,
  TextField,
} from "@radix-ui/themes";
import { Users, Copy, Check } from "lucide-react";
import toast from "react-hot-toast";
import Modal from "@/components/ui/Modal";
import SideDrawer from "@/components/ui/SideDrawer";
import DataGrid, { DataGridColumn } from "@/components/ui/DataGrid";
import { LoadingScreen } from "@/components/ui/LoadingScreen";
import { Offer, OFFER_STATUSES } from "@/types/breeze/AnythinkPay";
import { PaginatedResult } from "@/types/breeze/Any";
import {
  useAnythinkPayOffers,
  useAnythinkPayOfferCodes,
  useAnythinkPayOfferRedemptions,
  useCreateAnythinkPayOffer,
  useUpdateAnythinkPayOffer,
} from "@/hooks/useAnythinkPayOffers";
import { formatDate, formatRelativeDate } from "@/utils/date";
import {
  EmptyStateCard,
  RewardForm,
  RewardPickerSection,
  StatusBadge,
  parseReward,
  rewardStatusColor,
  rewardSummary,
  serialiseReward,
} from "./_shared";
import Select from "@/components/ui/Select";

type ProgrammeFormState = {
  name: string;
  description: string;
  redeemer: RewardForm;
  referrer: RewardForm;
  per_user_redemption_cap: number;
  status: "active" | "paused" | "expired";
};

const emptyForm = (): ProgrammeFormState => ({
  name: "",
  description: "",
  redeemer: { type: "trial_extension", days: 14 },
  referrer: { type: "trial_extension", days: 14 },
  per_user_redemption_cap: 1,
  status: "active",
});

const formFromOffer = (offer: Offer): ProgrammeFormState => ({
  name: offer.name,
  description: offer.description ?? "",
  redeemer: parseReward(offer.redeemer_reward_json) ?? { type: "none" },
  referrer: parseReward(offer.referrer_reward_json) ?? { type: "none" },
  per_user_redemption_cap: offer.per_user_redemption_cap,
  status: offer.status,
});

type ReferralsProps = {
  isCreateOpen?: boolean;
  onCreateOpenChange?: (open: boolean) => void;
};

export default function Referrals({
  isCreateOpen,
  onCreateOpenChange,
}: ReferralsProps = {}) {
  const { data: offers, isLoading } = useAnythinkPayOffers();
  const [internalCreateOpen, setInternalCreateOpen] = useState(false);
  const createOpen = isCreateOpen ?? internalCreateOpen;
  const setCreateOpen = onCreateOpenChange ?? setInternalCreateOpen;
  const [activeOffer, setActiveOffer] = useState<Offer | null>(null);

  const programmes = useMemo(
    () => (offers ?? []).filter((o) => o.kind === "referral"),
    [offers],
  );

  if (isLoading) return <LoadingScreen fullPage={false} />;

  if (programmes.length === 0) {
    return (
      <>
        <EmptyStateCard
          icon={<Users size={36} />}
          title="No referral programmes yet"
          description="Reward users for inviting other people. Each member gets their own auto-generated code, and you decide what they (and the new joiner) get when it's redeemed."
          cta={null}
        />
        <ProgrammeCreateModal open={createOpen} onClose={() => setCreateOpen(false)} />
      </>
    );
  }

  if (programmes.length === 1) {
    return (
      <>
        <SingleProgrammeView offer={programmes[0]} />
        <ProgrammeCreateModal open={createOpen} onClose={() => setCreateOpen(false)} />
      </>
    );
  }

  const columns: DataGridColumn<Offer>[] = [
    {
      field: "name",
      header: "Name",
      flex: 1.4,
      minWidth: 220,
      cellRenderer: (o) => (
        <Flex direction="column">
          <Text size="2" weight="medium">{o.name}</Text>
          {o.description && (
            <Text size="1" color="gray">{o.description}</Text>
          )}
        </Flex>
      ),
      valueGetter: (o) => o.name,
    },
    {
      field: "redeemer_reward",
      header: "Redeemer gets",
      flex: 1,
      minWidth: 150,
      cellRenderer: (o) => <Text size="2">{rewardSummary(o.redeemer_reward_json)}</Text>,
      valueGetter: (o) => rewardSummary(o.redeemer_reward_json),
    },
    {
      field: "referrer_reward",
      header: "Referrer gets",
      flex: 1,
      minWidth: 150,
      cellRenderer: (o) => <Text size="2">{rewardSummary(o.referrer_reward_json)}</Text>,
      valueGetter: (o) => rewardSummary(o.referrer_reward_json),
    },
    {
      field: "status",
      header: "Status",
      flex: 0.7,
      minWidth: 110,
      cellRenderer: (o) => <StatusBadge status={o.status} />,
    },
    {
      field: "created_at",
      header: "Created",
      flex: 0.9,
      minWidth: 130,
      cellRenderer: (o) => (
        <Text size="2" color="gray">{formatRelativeDate(o.created_at)}</Text>
      ),
    },
  ];

  const paginated: PaginatedResult<Offer> = {
    items: programmes,
    page: 1,
    page_size: programmes.length,
    total_items: programmes.length,
    total_pages: 1,
    has_next_page: false,
    has_previous_page: false,
  };

  return (
    <Flex direction="column" gap="4">
      <Box style={{ height: 480 }}>
        <DataGrid<Offer>
          data={paginated}
          columns={columns}
          storageKey="referral-programmes-list"
          currentPage={1}
          currentPageSize={programmes.length}
          onPageChange={() => {}}
          onPageSizeChange={() => {}}
          rowSelection="none"
          showPagination={false}
          onCellClicked={(_, o) => setActiveOffer(o)}
        />
      </Box>

      <ProgrammeCreateModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <ProgrammeDetailDrawer
        offer={activeOffer}
        onClose={() => setActiveOffer(null)}
      />
    </Flex>
  );
}

function ProgrammeCreateModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const create = useCreateAnythinkPayOffer();
  const [state, setState] = useState<ProgrammeFormState>(emptyForm());

  useEffect(() => {
    if (open) setState(emptyForm());
  }, [open]);

  const submit = async () => {
    if (!state.name.trim()) {
      toast.error("Name is required");
      return;
    }
    const redeemerJson = serialiseReward(state.redeemer);
    if (!redeemerJson) {
      toast.error("Choose what the new joiner gets");
      return;
    }
    const referrerJson = serialiseReward(state.referrer);

    try {
      await create.mutateAsync({
        name: state.name.trim(),
        description: state.description.trim() || null,
        kind: "referral",
        redeemer_reward_json: redeemerJson,
        referrer_reward_json: referrerJson,
        per_user_redemption_cap: state.per_user_redemption_cap,
        status: state.status,
      });
      onClose();
    } catch {
      // toast handled in service
    }
  };

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title="New referral programme"
      size="md"
      modal={false}
      actions={
        <Flex gap="2" justify="end">
          <Button variant="soft" color="gray" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} loading={create.isPending}>
            Create programme
          </Button>
        </Flex>
      }
    >
      <ProgrammeFormBody state={state} setState={setState} />
    </Modal>
  );
}

function ProgrammeFormBody({
  state,
  setState,
  usePortal = false,
}: {
  state: ProgrammeFormState;
  setState: (s: ProgrammeFormState) => void;
  usePortal?: boolean;
}) {
  return (
    <Flex direction="column" gap="4" p="2">
      <Box>
        <Text size="2" weight="medium" as="div" mb="1">Name</Text>
        <TextField.Root
          value={state.name}
          onChange={(e) => setState({ ...state, name: e.target.value })}
          placeholder="Friend referral"
        />
      </Box>

      <Box>
        <Text size="2" weight="medium" as="div" mb="1">Description</Text>
        <TextArea
          value={state.description}
          onChange={(e) => setState({ ...state, description: e.target.value })}
          placeholder="Optional internal note"
          rows={2}
        />
      </Box>

      <Grid columns="2" gap="3">
        <Box>
          <Text size="2" weight="medium" as="div" mb="1">Per-user redemption cap</Text>
          <TextField.Root
            type="number"
            min="1"
            value={state.per_user_redemption_cap.toString()}
            onChange={(e) =>
              setState({
                ...state,
                per_user_redemption_cap: parseInt(e.target.value || "1", 10),
              })
            }
          />
        </Box>
        <Box>
          <Text size="2" weight="medium" as="div" mb="1">Status</Text>
          <Select
            value={state.status}
            onChange={(v) =>
              setState({ ...state, status: v as ProgrammeFormState["status"] })
            }
            options={OFFER_STATUSES.map((s) => ({ value: s.value, label: s.label }))}
            usePortal={usePortal}
          />
        </Box>
      </Grid>

      <RewardPickerSection
        label="The new joiner gets"
        reward={state.redeemer}
        onChange={(r) => setState({ ...state, redeemer: r })}
        audience="redeemer"
        usePortal={usePortal}
      />

      <RewardPickerSection
        label="The referrer gets when someone redeems their code"
        reward={state.referrer}
        onChange={(r) => setState({ ...state, referrer: r })}
        audience="referrer"
        usePortal={usePortal}
      />
    </Flex>
  );
}

function SingleProgrammeView({ offer }: { offer: Offer }) {
  const [tab, setTab] = useState("settings");
  const [editState, setEditState] = useState<ProgrammeFormState>(formFromOffer(offer));
  const update = useUpdateAnythinkPayOffer();

  useEffect(() => {
    setEditState(formFromOffer(offer));
  }, [offer]);

  const submitEdit = async () => {
    if (!editState.name.trim()) {
      toast.error("Name is required");
      return;
    }
    const redeemerJson = serialiseReward(editState.redeemer);
    if (!redeemerJson) {
      toast.error("Choose what the new joiner gets");
      return;
    }
    const referrerJson = serialiseReward(editState.referrer);

    try {
      await update.mutateAsync({
        id: offer.id,
        name: editState.name.trim(),
        description: editState.description.trim() || null,
        redeemer_reward_json: redeemerJson,
        referrer_reward_json: referrerJson,
        per_user_redemption_cap: editState.per_user_redemption_cap,
        status: editState.status,
      });
    } catch {
      // toast handled in service
    }
  };

  return (
    <Card>
      <Flex direction="column" gap="3" p="3">
        <Flex justify="between" align="center">
          <Flex direction="column">
            <Text size="3" weight="medium">{offer.name}</Text>
            {offer.description && (
              <Text size="1" color="gray">{offer.description}</Text>
            )}
          </Flex>
          <StatusBadge status={offer.status} />
        </Flex>

        <Tabs.Root value={tab} onValueChange={setTab}>
          <Tabs.List>
            <Tabs.Trigger value="settings">Settings</Tabs.Trigger>
            <Tabs.Trigger value="members">Members</Tabs.Trigger>
            <Tabs.Trigger value="redemptions">Redemptions</Tabs.Trigger>
          </Tabs.List>

          <Box mt="4">
            <Tabs.Content value="settings">
              <ProgrammeFormBody state={editState} setState={setEditState} usePortal />
              <Flex justify="end" mt="4">
                <Button onClick={submitEdit} loading={update.isPending}>
                  Save changes
                </Button>
              </Flex>
            </Tabs.Content>
            <Tabs.Content value="members">
              <MembersTab offer={offer} />
            </Tabs.Content>
            <Tabs.Content value="redemptions">
              <RedemptionsTab offerId={offer.id} />
            </Tabs.Content>
          </Box>
        </Tabs.Root>
      </Flex>
    </Card>
  );
}

function ProgrammeDetailDrawer({
  offer,
  onClose,
}: {
  offer: Offer | null;
  onClose: () => void;
}) {
  const [tab, setTab] = useState("members");
  const [editState, setEditState] = useState<ProgrammeFormState | null>(null);
  const update = useUpdateAnythinkPayOffer();

  useEffect(() => {
    if (offer) {
      setEditState(formFromOffer(offer));
      setTab("members");
    }
  }, [offer]);

  const submitEdit = async () => {
    if (!offer || !editState) return;
    if (!editState.name.trim()) {
      toast.error("Name is required");
      return;
    }
    const redeemerJson = serialiseReward(editState.redeemer);
    if (!redeemerJson) {
      toast.error("Choose what the new joiner gets");
      return;
    }
    const referrerJson = serialiseReward(editState.referrer);

    try {
      await update.mutateAsync({
        id: offer.id,
        name: editState.name.trim(),
        description: editState.description.trim() || null,
        redeemer_reward_json: redeemerJson,
        referrer_reward_json: referrerJson,
        per_user_redemption_cap: editState.per_user_redemption_cap,
        status: editState.status,
      });
    } catch {
      // toast handled in service
    }
  };

  return (
    <SideDrawer
      isOpen={!!offer}
      onClose={onClose}
      title={offer?.name ?? ""}
      width="max-w-2xl"
    >
      {offer && editState && (
        <Tabs.Root value={tab} onValueChange={setTab}>
          <Tabs.List>
            <Tabs.Trigger value="members">Members</Tabs.Trigger>
            <Tabs.Trigger value="redemptions">Redemptions</Tabs.Trigger>
            <Tabs.Trigger value="settings">Settings</Tabs.Trigger>
          </Tabs.List>

          <Box mt="4">
            <Tabs.Content value="members">
              <MembersTab offer={offer} />
            </Tabs.Content>
            <Tabs.Content value="redemptions">
              <RedemptionsTab offerId={offer.id} />
            </Tabs.Content>
            <Tabs.Content value="settings">
              <ProgrammeFormBody state={editState} setState={setEditState} />
              <Flex justify="end" mt="4">
                <Button onClick={submitEdit} loading={update.isPending}>
                  Save changes
                </Button>
              </Flex>
            </Tabs.Content>
          </Box>
        </Tabs.Root>
      )}
    </SideDrawer>
  );
}

function MembersTab({ offer }: { offer: Offer }) {
  const { data: codes, isLoading } = useAnythinkPayOfferCodes(offer.id);
  const [copied, setCopied] = useState<string | null>(null);

  if (isLoading) return <LoadingScreen fullPage={false} />;

  const personal = (codes ?? []).filter((c) => c.owner_user_id !== null);

  if (personal.length === 0) {
    return (
      <Text size="2" color="gray">
        No members have generated their personal code yet. Each user's code is
        generated on first request to the API or via the Users table.
      </Text>
    );
  }

  const copy = async (slug: string) => {
    try {
      await navigator.clipboard.writeText(slug);
      setCopied(slug);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      toast.error("Failed to copy");
    }
  };

  return (
    <Table.Root variant="surface">
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeaderCell>User</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Code</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Redemptions</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Generated</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell width="50px"></Table.ColumnHeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {personal.map((c) => (
          <Table.Row key={c.id}>
            <Table.Cell>
              <Flex direction="column">
                <Text size="2" weight="medium">
                  {c.owner
                    ? `${c.owner.first_name} ${c.owner.last_name}`.trim()
                    : `User #${c.owner_user_id}`}
                </Text>
                {c.owner?.email && (
                  <Text size="1" color="gray">{c.owner.email}</Text>
                )}
              </Flex>
            </Table.Cell>
            <Table.Cell>
              <Badge size="1" variant="soft" color="indigo" style={{ fontFamily: "monospace" }}>
                {c.slug}
              </Badge>
            </Table.Cell>
            <Table.Cell><Text size="2">{c.redemption_count}</Text></Table.Cell>
            <Table.Cell>
              <Text size="2" color="gray">{formatRelativeDate(c.created_at)}</Text>
            </Table.Cell>
            <Table.Cell>
              <IconButton variant="ghost" size="1" onClick={() => copy(c.slug)} title="Copy code">
                {copied === c.slug ? <Check size={14} /> : <Copy size={14} />}
              </IconButton>
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table.Root>
  );
}

function RedemptionsTab({ offerId }: { offerId: string }) {
  const { data: redemptions, isLoading } = useAnythinkPayOfferRedemptions(offerId);

  if (isLoading) return <LoadingScreen fullPage={false} />;
  if ((redemptions?.length ?? 0) === 0)
    return <Text size="2" color="gray">No redemptions yet.</Text>;

  return (
    <Table.Root variant="surface">
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeaderCell>Redeemer</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Redeemer reward</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Referrer</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Referrer reward</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>When</Table.ColumnHeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {(redemptions ?? []).map((r) => (
          <Table.Row key={r.id}>
            <Table.Cell>
              <Text size="2">
                {r.redeemer
                  ? `${r.redeemer.first_name} ${r.redeemer.last_name}`.trim()
                  : `#${r.redeemer_user_id}`}
              </Text>
            </Table.Cell>
            <Table.Cell>
              <Badge color={rewardStatusColor(r.redeemer_reward_status)} variant="soft">
                {r.redeemer_reward_status}
              </Badge>
            </Table.Cell>
            <Table.Cell>
              <Text size="2">
                {r.referrer
                  ? `${r.referrer.first_name} ${r.referrer.last_name}`.trim()
                  : r.referrer_user_id
                  ? `#${r.referrer_user_id}`
                  : "—"}
              </Text>
            </Table.Cell>
            <Table.Cell>
              {r.referrer_reward_status ? (
                <Badge color={rewardStatusColor(r.referrer_reward_status)} variant="soft">
                  {r.referrer_reward_status}
                </Badge>
              ) : (
                <Text size="1" color="gray">—</Text>
              )}
            </Table.Cell>
            <Table.Cell>
              <Text size="2" color="gray">{formatDate(r.redeemed_at)}</Text>
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table.Root>
  );
}
